// packages/charts/src/react/formatValue.ts
//
// Display formatting for chart values, driven by the `ChartMeta` envelope
// the backend hook_resolver emits alongside every payload.
//
// WHY THIS MIRRORS PYTHON
// -----------------------
// A report page renders two kinds of number side by side. The tile values
// (`<DataPoint value=…>`) are formatted server-side by
// `services/reports/metric_formatting.py::format_metric_value` and arrive
// as finished strings. Chart axes, tooltips and end labels are formatted
// here, in the browser. The reader sees both in one glance, so this module
// is a deliberate port of that Python function — same magnitude tiers,
// same decimals, same uppercase K/M/B, same en-US grouping. Change one
// side and `__tests__/pythonParity.test.ts` goes red on purpose.
//
// DISCRIMINATE ON `format`, NEVER ON `unit`
// -----------------------------------------
// `metric_unit_symbol()` returns "%" for BOTH `percentage` (already 0..100)
// and `fraction` (still 0..1). A unit-based branch therefore renders a
// 2.69% conversion rate as "0.0%" — that bug shipped on the line-chart end
// labels in cb2ddeb7. The format discriminator is the only safe one; unit
// is a fallback for payloads that carry no format at all.

import type { ChartMeta } from "./types";

/**
 * `full` — parity with the Python formatter. Use wherever the number is a
 *   value the reader compares against the surrounding text: tooltips, end
 *   labels, data labels.
 * `axis` — the same number as a repeated scale marker. Trailing ".0" is
 *   dropped and sub-thousand values lose their decimals, because five ticks
 *   reading "€500.0K / €400.0K / …" are noise, and nobody reads an axis
 *   tick as a measured value.
 */
export type ChartValueMode = "axis" | "full";

/** Python's `format_metric_value` takes a required `decimals`; the envelope
 * may omit it. These are the per-format defaults its callers use. */
const DEFAULT_DECIMALS: Record<string, number> = {
  currency: 2,
  percentage: 1,
  fraction: 1,
  number: 0,
  count: 0,
};

/** en-US grouping, pinned to match the Python side's locale pinning. */
function grouped(value: number, decimals: number): string {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function trimZero(s: string): string {
  return s.endsWith(".0") || s.endsWith(".00") ? s.replace(/\.0+$/, "") : s;
}

/** Magnitude tiers, decimals per tier taken from `_format_currency`. */
function abbreviate(abs: number, mode: ChartValueMode): string | null {
  if (abs >= 1_000_000_000) return tier(abs / 1_000_000_000, 2, "B", mode);
  if (abs >= 1_000_000) return tier(abs / 1_000_000, 2, "M", mode);
  if (abs >= 1_000) return tier(abs / 1_000, 1, "K", mode);
  return null;
}

function tier(scaled: number, decimals: number, suffix: string, mode: ChartValueMode): string {
  const body = scaled.toFixed(decimals);
  return `${mode === "axis" ? trimZero(body) : body}${suffix}`;
}

function formatCurrency(value: number, decimals: number, mode: ChartValueMode): string {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  // Sign is prefixed ("-€1.24M"), not embedded ("€-1.24M") — as in Python.
  const abbreviated = abbreviate(abs, mode);
  if (abbreviated) return `${sign}€${abbreviated}`;
  return `${sign}€${grouped(abs, mode === "axis" ? 0 : decimals)}`;
}

function formatPlainNumber(value: number, decimals: number, mode: ChartValueMode): string {
  if (mode === "axis") {
    // Python never abbreviates counts — a tile shows "495,120". An axis
    // cannot afford that width five times over, so it does.
    const sign = value < 0 ? "-" : "";
    const abbreviated = abbreviate(Math.abs(value), mode);
    if (abbreviated) return `${sign}${abbreviated}`;
    return grouped(value, 0);
  }
  return grouped(value, decimals);
}

/**
 * Format a chart value for display.
 *
 * Non-finite input returns an empty string rather than "NaN" — a gap in the
 * data must read as a gap.
 */
export function formatChartValue(
  value: unknown,
  meta?: ChartMeta,
  mode: ChartValueMode = "full",
): string {
  // `Number(null)` is 0 and `Number("")` is 0 — a gap in the series would
  // render as "€0", which is a number the report never measured.
  if (value === null || value === undefined || value === "") return "";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "";

  const fmt = (meta?.format ?? "").toLowerCase();
  const decimals = meta?.decimals ?? DEFAULT_DECIMALS[fmt] ?? 0;

  if (fmt === "currency") return formatCurrency(n, decimals, mode);
  if (fmt === "percentage") {
    const body = n.toFixed(decimals);
    return `${mode === "axis" ? trimZero(body) : body}%`;
  }
  if (fmt === "fraction") {
    // Fractions are stored 0..1; scale to 0..100 for display. This is the
    // branch a unit-based discriminator silently skipped.
    const body = (n * 100).toFixed(decimals);
    return `${mode === "axis" ? trimZero(body) : body}%`;
  }
  if (fmt === "number" || fmt === "count") return formatPlainNumber(n, decimals, mode);

  // No (or unknown) format — fall back to the unit symbol. "%" here can
  // only be guessed as already-scaled; a fraction without a format is
  // indistinguishable from a small percentage, which is exactly why the
  // backend always sends a format.
  const unit = meta?.unit ?? "";
  if (unit === "%") {
    const body = n.toFixed(decimals || 1);
    return `${mode === "axis" ? trimZero(body) : body}%`;
  }

  const sign = n < 0 ? "-" : "";
  const abbreviated = abbreviate(Math.abs(n), mode);
  const body = abbreviated ? `${sign}${abbreviated}` : grouped(n, decimals);
  if (unit === "€" || unit === "$" || unit === "£") return `${sign}${unit}${body.slice(sign.length)}`;
  return unit ? `${body} ${unit}` : body;
}
